import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import {
  actionMeta,
  actionStatusMeta,
  CORE_ACTIONS,
  fetchActionStats,
  listCoreActions,
  runCoreAction,
  type CoreAction,
  type CoreActionResult,
} from '@/lib/coreActions';
import { generateIdempotencyKey } from '@/lib/coreIdempotency';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ActionLayerTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [actions, setActions] = useState<CoreAction[]>([]);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0, denied: 0, duplicate: 0, byAction: {} as Record<string, number> });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState('create_customer');
  const [useIdem, setUseIdem] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CoreActionResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [a, s] = await Promise.all([listCoreActions(org.id, { limit: 80 }), fetchActionStats(org.id)]);
      setActions(a);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const def = actionMeta(selected);
      const res = await runCoreAction({
        actionKey: selected,
        tenantId: org.tenant_id,
        organizationId: org.id,
        actorUserId: user?.id ?? null,
        actorName: user?.name || user?.user_name || 'Utilisateur',
        parameters: { demo: true, at: new Date().toISOString() },
        canAccess: (p) => has(p),
        idempotencyKey: useIdem ? generateIdempotencyKey('test') : null,
        idempotencyScope: def.idempotencyScope,
        source: 'dashboard',
        execute: async () => ({ entityType: def.resourceType, entityId: Math.floor(100000 + Math.random() * 899999), result: { simulated: true } }),
      });
      setResult(res);
      load();
    } finally {
      setRunning(false);
    }
  };

  const deniedRate = stats.total > 0 ? Math.round((stats.denied / stats.total) * 100) : 0;
  const duplicateRate = stats.total > 0 ? Math.round((stats.duplicate / stats.total) * 100) : 0;
  const recentEffects = actions.filter((a) => a.status === 'denied' || a.status === 'duplicate').slice(0, 6);

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Action Layer</span> : la fondation qui décrit, exécute et journalise chaque opération métier —
        <code className="bg-background-100 px-1 rounded mx-1">action_id · actor · resource · permission · status · result · timestamp</code>.
        Chaque action passe par le contrôle de permission puis est journalisée.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Actions journalisées', value: stats.total, icon: 'ri-flashlight-line', color: 'text-primary-600' },
          { label: 'Succès', value: stats.success, icon: 'ri-checkbox-circle-line', color: 'text-primary-600' },
          { label: 'Rejouées (idempotent)', value: stats.duplicate, icon: 'ri-loop-right-line', color: 'text-secondary-600' },
          { label: 'Refusées', value: stats.denied, icon: 'ri-shield-cross-line', color: 'text-accent-600' },
        ].map((c) => (
          <div key={c.label} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
            <i className={`${c.icon} text-lg ${c.color}`}></i>
            <p className="text-lg font-bold text-foreground-900 mt-1">{c.value}</p>
            <p className="text-xs text-foreground-500">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Effet réel de l’autorisation & de l’idempotence */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-shield-check-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Effet réel de l’autorisation &amp; de l’idempotence</h4>
          <span className="text-[11px] text-foreground-400 ml-auto">{stats.total} action(s) au total</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-background-200/60">
          <div className="p-4">
            <p className="text-xs font-medium text-foreground-500 flex items-center gap-1.5"><i className="ri-shield-cross-line text-red-500"></i> Refusées (autorisation)</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">{stats.denied}</p>
            <p className="text-[11px] text-foreground-400 mt-0.5">{deniedRate}% des actions — permission manquante, écriture bloquée avant toute exécution.</p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium text-foreground-500 flex items-center gap-1.5"><i className="ri-loop-right-line text-secondary-600"></i> Rejouées (idempotence)</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">{stats.duplicate}</p>
            <p className="text-[11px] text-foreground-400 mt-0.5">{duplicateRate}% des actions — rejeu détecté, réponse mémorisée, aucun doublon créé.</p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium text-foreground-500 flex items-center gap-1.5"><i className="ri-error-warning-line text-accent-600"></i> Échouées (métier)</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">{stats.failed}</p>
            <p className="text-[11px] text-foreground-400 mt-0.5">Erreur d’exécution (validation, contrainte…). Rejouables après correction.</p>
          </div>
        </div>
        {recentEffects.length > 0 && (
          <div className="border-t border-background-200/60 px-4 py-2.5">
            <p className="text-[11px] font-medium text-foreground-500 mb-1.5">Derniers effets observés</p>
            <div className="flex flex-wrap gap-1.5">
              {recentEffects.map((a) => {
                const meta = actionStatusMeta(a.status);
                return (
                  <span key={a.id} className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>
                    {a.action_key} · {meta.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Catalogue */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-apps-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Actions standard du socle</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-background-200/60">
          {CORE_ACTIONS.map((a) => (
            <div key={a.actionKey} className="flex items-center gap-3 px-4 py-3 border-b border-background-200/60">
              <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0"><i className={a.icon}></i></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-800 truncate">{a.label}</p>
                <p className="text-[11px] text-foreground-400 truncate"><code className="bg-background-100 px-1 rounded">{a.actionKey}</code> · {a.permission}</p>
              </div>
              {a.idempotent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">idempotent</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Testeur */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Tester une action</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Action</span>
              <select className={INPUT} value={selected} onChange={(e) => { setSelected(e.target.value); setResult(null); }}>
                {CORE_ACTIONS.map((a) => <option key={a.actionKey} value={a.actionKey}>{a.actionKey}</option>)}
              </select>
            </label>
            <label className="flex items-end gap-2 pb-2">
              <input type="checkbox" checked={useIdem} onChange={(e) => setUseIdem(e.target.checked)} className="w-4 h-4 accent-primary-500 cursor-pointer" />
              <span className="text-xs text-foreground-600">Clé d’idempotence (évite le double traitement)</span>
            </label>
          </div>
          <p className="text-[11px] text-foreground-400">
            Permission requise : <span className="font-medium text-foreground-600">{actionMeta(selected).permission}</span>
            {has(actionMeta(selected).permission) ? ' — vous y avez accès ✓' : ' — refusé pour votre rôle ✗'}
          </p>
          <button onClick={run} disabled={running} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-flashlight-line"></i>} Exécuter
          </button>

          {result && (
            <div className={`rounded-md border p-3 text-xs ${result.ok ? 'bg-primary-50 border-primary-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`font-semibold flex items-center gap-1.5 ${result.ok ? 'text-primary-800' : 'text-red-700'}`}>
                <i className={result.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                {result.status} · {result.actionId}
                {result.status === 'duplicate' && <span className="font-normal">(action déjà exécutée — réponse mémorisée)</span>}
              </p>
              <p className="mt-1 text-foreground-600">
                Ressource produite : {result.entityType || '—'} #{result.entityId || '—'} · {result.durationMs} ms
              </p>
              {result.error && <p className="mt-1 text-red-600">{result.error}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Journal */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Journal des actions</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : actions.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune action journalisée. Lancez un test ci-dessus.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[420px] overflow-y-auto">
            {actions.map((a) => {
              const meta = actionStatusMeta(a.status);
              return (
                <div key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                  <i className={`${actionMeta(a.action_key).icon} text-foreground-500`}></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-800 truncate">
                      {a.action_key} <span className="font-normal text-foreground-400">· {a.actor_name || 'système'}</span>
                    </p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {a.resource_type}#{a.resource_id || '—'}
                      {a.error ? ` · ${a.error}` : ''}
                      {a.created_at ? ` · ${new Date(a.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}